import { Injectable } from '@angular/core';
import { ReportType } from '../constants-types/Enums';
import { PI } from '../constants-types/PI.model';
import {
    DreamTeamFunctionType,
    DreamTeamCrossFunctionId,
    TeamImprovementId
} from '../constants-types/StrategyConstants';
import {
    CoachingGuideConfig,
    CoachingGuideReportRequest
} from '../core-data-model/CoachingGuideConfig.model';
import { EmailSharingCommand } from '../core-data-model/EmailList.model';
import {
    DreamTeamActionsDefinition,
    DreamTeamInsights,
    Group,
    GroupItemUpdate,
    GroupUpdate,
    GroupValidateResponse
} from '../core-data-model/Group.model';
import {
    TeamServerEntry,
    TeamCreatePost,
    SharedGroupsUsersFoldersPost,
    SharedGroupsForUserServer,
    SharedGroupsGetUsersForGroupServer,
    SharedGroupsValidatePost,
    SharedGroupsValidateServer,
    SharedGroupsGrantUserAccessResponse,
    DreamTeamGetTeamInsightsServer,
    DreamTeamGetTeamActionsDefinitionServer
} from '../core-data-model/GroupsApi.serverinterface';
import { RelationshipGuideConfig } from '../core-data-model/RelationshipguideConfig.model';
import { PeopleAndJobsSearchResult } from '../core-data-model/SearchApi.serverinterface';
import { SearchState } from '../core-data-model/SearchState.model';
import { User } from '../core-data-model/User.model';
import { AsyncFolderDecorator } from '../core-services/Folders.dataservice';
import { GroupTransformers } from '../core-services/GroupsApi.datatransform';
import { PeopleAndJobsQueryGeneratorService } from '../core-services/PeopleAndJobsQueryGenerator.service';
import { SearchTransformers } from '../core-services/SearchApi.datatransform';
import { HttpHeaderContentType } from '../shared';
import { ModuleAccessService } from '../system-services';
import { ApiService } from '../system-services/Api.service';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings';
import { ReportUtilities } from '../text-utilities/Report.utility';
import { TranslationService } from '../translations';

@Injectable({
    providedIn: 'root'
})
export class TeamsApi {
    static getDreamTeamActionsDefinitionPromise: Promise<DreamTeamActionsDefinition>;

    // static class members to control how to call the TeamInsights Get API lazily
    // and after 24 hours have elapsed.
    static getDreamTeamInsightsPromise: Promise<DreamTeamInsights>;
    static lastRetrievalTime: number; // last retrival time in millseconds
    static retrievalIntervalInMillisec: number = 24 * 60 * 60 * 1000; // one day
    static isLastRetrievalFailed = false;

    constructor(
        private readonly api: ApiService,
        private readonly userProfile: UserProfileSettingsService,
        private readonly transform: GroupTransformers,
        private readonly searchTransformers: SearchTransformers,
        private readonly peopleAndJobsQueryGeneratorService: PeopleAndJobsQueryGeneratorService,
        private readonly TR: TranslationService,
        private readonly reportUtil: ReportUtilities,
        private readonly moduleAccess: ModuleAccessService
    ) {}

    @AsyncFolderDecorator.waitForFolders()
    getGroupContents(
        id: string,
        pristineSearchState: SearchState,
        companyId: string = this.userProfile.currentCompany
    ) {
        return this.api.multiplexSearchRequest(
            pristineSearchState,
            (searchState) => {
                const oDataQuery =
                    this.peopleAndJobsQueryGeneratorService.createODataString(
                        searchState
                    );
                const { url, designApiUrl } = this.updateTeamsEndpoint(
                    `/api/v4/companies/${companyId}/teams/${id}/teamitems?${oDataQuery}`
                );
                return this.api
                    .get<PeopleAndJobsSearchResult>(
                        url,
                        null,
                        null,
                        designApiUrl
                    )
                    .then(([results]) => {
                        if (!results || !results.peopleAndJobsSearchResult) {
                            searchState.pageState.maxResults = 0;
                            return [];
                        }
                        pristineSearchState.pageState.maxResults =
                            results.totalSearchResults;
                        return results.peopleAndJobsSearchResult.map((person) =>
                            this.searchTransformers.transformPeopleAndJobs(
                                person
                            )
                        );
                    });
            }
        );
    }

    updateTeam(
        team: GroupUpdate,
        companyId: string = this.userProfile.currentCompany
    ) {
        const { url, designApiUrl } = this.updateTeamsEndpoint(
            `/api/v4/companies/${companyId}/teams/${team.originalObject.id}`
        );
        return this.api.patch(
            url,
            this.transform.transformClientToServerPatch(team),
            designApiUrl
        );
    }

    updateTeamItem(
        teamId: string,
        teamItem: GroupItemUpdate,
        companyId: string = this.userProfile.currentCompany
    ) {
        const { url, designApiUrl } = this.updateTeamsEndpoint(
            `/api/v4/companies/${companyId}/teams/${teamId}/teamitem`
        );
        return this.api.patch(
            url,
            this.transform.transformClientToServerPatchTeamItem(teamItem),
            designApiUrl
        );
    }

    deleteTeam(
        team: Group,
        companyId: string = this.userProfile.currentCompany
    ) {
        const { url, designApiUrl } = this.updateTeamsEndpoint(
            `/api/v4/companies/${companyId}/teams/${team.id}`
        );
        return this.api.delete(url, null, designApiUrl);
    }

    createTeam(
        teamName: string,
        teamFunctionType: DreamTeamFunctionType,
        otherTeamFunction: string,
        crossFunctionId: DreamTeamCrossFunctionId,
        teamImprovementIds: TeamImprovementId[],
        otherTeamImprovement: string,
        companyId: string = this.userProfile.currentCompany
    ) {
        const { url, designApiUrl } = this.updateTeamsEndpoint(
            `/api/v4/companies/${companyId}/teams`
        );
        return this.api
            .post<TeamCreatePost, TeamServerEntry>(
                url,
                {
                    teamName: teamName,
                    teamFunctionType: teamFunctionType,
                    otherTeamFunction: otherTeamFunction,
                    crossFunctionId: crossFunctionId,
                    teamImprovementIds: teamImprovementIds,
                    otherTeamImprovement: otherTeamImprovement
                },
                designApiUrl
            )
            .then(([server]) => this.transform.transformServerToClient(server));
    }

    validateSharedGroups(
        validate: SharedGroupsValidatePost,
        companyId: string = this.userProfile.currentCompany
    ): Promise<GroupValidateResponse[]> {
        return this.api
            .post<
                SharedGroupsValidatePost,
                SharedGroupsValidateServer
            >(`/api/v4/companies/${companyId}/sharedgroup/access`, validate)
            .then(([response]) =>
                this.transform.transformValidateResponseServerToClient(response)
            );
    }

    grantUsersAccessToSharedGroup(
        usersFolders: SharedGroupsUsersFoldersPost,
        companyId: string = this.userProfile.currentCompany
    ) {
        return this.api
            .post<
                SharedGroupsUsersFoldersPost,
                SharedGroupsGrantUserAccessResponse
            >(`/api/v4/companies/${companyId}/sharedgroup/sharegroupswithusers`, usersFolders)
            .then(([response]) => response);
    }

    revokeUserAccessToSharedGroup(
        usersFolders: SharedGroupsUsersFoldersPost,
        companyId: string = this.userProfile.currentCompany
    ) {
        return this.api
            .delete(
                `/api/v4/companies/${companyId}/sharedgroup/unsharegroupsfromusers`,
                usersFolders
            )
            .then((response) => response);
    }

    getUsersWithGroupAccess(
        groupId: string,
        companyId: string = this.userProfile.currentCompany
    ): Promise<User[]> {
        return this.api
            .get<SharedGroupsGetUsersForGroupServer>(
                `/api/v4/companies/${companyId}/sharedgroup/getusers?groupId=${groupId}`
            )
            .then(([response]) =>
                this.transform.transformUserAccessServerToClient(response)
            );
    }

    getSharedGroupsForUser(
        companyId: string = this.userProfile.currentCompany
    ): Promise<Group[]> {
        return this.api
            .get<SharedGroupsForUserServer>(
                `/api/v4/companies/${companyId}/sharedgroup`
            )
            .then(([response]) =>
                response.myGroupDetailReturns.map((resp) =>
                    this.transform.transformServerToClient(resp)
                )
            );
    }

    getAllGroupsInCompany(
        companyId: string = this.userProfile.currentCompany
    ): Promise<Group[]> {
        return this.api
            .get<SharedGroupsForUserServer>(
                `/api/v4/companies/${companyId}/allgroup`
            )
            .then(([response]) =>
                response.myGroupDetailReturns.map((resp) =>
                    this.transform.transformServerToClient(resp)
                )
            );
    }

    getCoachingGuideReport({
        coachingGuideConfig,
        emailSettings = null,
        companyId = this.userProfile.currentCompany,
        languageLocale = this.TR.currentLocale
    }: CoachingGuideReportRequest) {
        if (emailSettings) {
            return this.emailCoachingGuideReport(
                coachingGuideConfig,
                emailSettings,
                languageLocale
            );
        }
        return this.api.getResourceUrlWithPdfReference(
            `/api/v3/myGroups/${companyId}/PrintCoachingGuide`,
            HttpHeaderContentType.PDF,
            true,
            {
                coachingGuideConfig,
                languageLocale
            }
        );
    }

    getRelationshipGuideReport(
        relationshipGuideConfig: RelationshipGuideConfig,
        emailSettings: EmailSharingCommand = null,
        languageLocale: string = this.TR.currentLocale,
        companyId: string = this.userProfile.currentCompany
    ) {
        if (emailSettings) {
            return this.emailRelationshipGuideReport(
                relationshipGuideConfig,
                emailSettings,
                languageLocale
            );
        }
        return this.api.getResourceUrlWithPdfReference(
            `/api/v3/myGroups/${companyId}/PrintRelationshipGuide`,
            HttpHeaderContentType.PDF,
            true,
            {
                relationshipGuideConfig,
                languageLocale
            }
        );
    }

    getDreamTeamInsights(): Promise<DreamTeamInsights> {
        if (
            !TeamsApi.getDreamTeamInsightsPromise ||
            !TeamsApi.lastRetrievalTime ||
            TeamsApi.isLastRetrievalFailed ||
            Date.now() - TeamsApi.lastRetrievalTime >
                TeamsApi.retrievalIntervalInMillisec
        ) {
            TeamsApi.lastRetrievalTime = Date.now();
            const { url, designApiUrl } = this.updateTeamsEndpoint(
                `/api/v4/teams/teaminsights`
            );
            TeamsApi.getDreamTeamInsightsPromise = this.api
                .get<DreamTeamGetTeamInsightsServer>(
                    url,
                    null,
                    null,
                    designApiUrl
                )
                .then(([server, response]) => {
                    if (response.ok) {
                        TeamsApi.isLastRetrievalFailed = false;
                        return this.transform.transformTeamInsightsServerToClient(
                            server
                        );
                    } else {
                        TeamsApi.isLastRetrievalFailed = true;
                        return null;
                    }
                })
                .catch((err) => {
                    TeamsApi.isLastRetrievalFailed = true;
                    return null;
                });
        }
        return TeamsApi.getDreamTeamInsightsPromise;
    }

    getDreamTeamActionsDefinition(): Promise<DreamTeamActionsDefinition> {
        const { url, designApiUrl } = this.updateTeamsEndpoint(
            `/api/v4/teams/teamactionsdefinition`
        );
        if (!TeamsApi.getDreamTeamActionsDefinitionPromise) {
            TeamsApi.getDreamTeamActionsDefinitionPromise = this.api
                .get<DreamTeamGetTeamActionsDefinitionServer>(
                    url,
                    null,
                    null,
                    designApiUrl
                )
                .then(([response]) =>
                    this.transform.transformTeamActionsDefinitionServerToClient(
                        response
                    )
                )
                .catch((err) => null);
        }

        return TeamsApi.getDreamTeamActionsDefinitionPromise;
    }

    private getReportEmail(emailSettings: EmailSharingCommand, payload: any) {
        if (emailSettings) {
            payload.emailDetails = {
                emailTo: emailSettings.emails.join(','),
                emailSubject: emailSettings.subject,
                emailMessageBody: emailSettings.message
            };
        }

        return this.api.getResourceUrlWithPdfReference(
            `/api/v3/toolbox/generateReport`,
            HttpHeaderContentType.PDF,
            true,
            payload
        );
    }

    private emailRelationshipGuideReport(
        relationshipGuideConfig: RelationshipGuideConfig,
        emailSettings: EmailSharingCommand = null,
        languageLocale: string = this.TR.currentLocale
    ) {
        const payload: any = {
            url: `imageservice/relationshipguide?languageLocale=${languageLocale}`,
            body: relationshipGuideConfig,
            fileName: this.reportUtil.getPersonReportName(
                ReportType.RelationshipGuide,
                relationshipGuideConfig.person1,
                relationshipGuideConfig.person2
            )
        };

        return this.getReportEmail(emailSettings, payload);
    }

    private emailCoachingGuideReport(
        coachingGuideConfig: CoachingGuideConfig,
        emailSettings: EmailSharingCommand = null,
        languageLocale: string = this.TR.currentLocale
    ) {
        const payload: any = {
            url: `imageservice/coachingguide?languageLocale=${languageLocale}`,
            body: coachingGuideConfig,
            fileName: this.reportUtil.getPersonReportName(
                ReportType.CoachingGuide,
                coachingGuideConfig.person
            )
        };

        return this.getReportEmail(emailSettings, payload);
    }

    /**
     * Adjust teams endpoint URL based on design feature flag.
     * If the URL contains '/teams' and the feature is enabled, return the updated URL
     * (by replacing /api/v4/ with /api/v5/) together with the design API host.
     */
    private updateTeamsEndpoint(url: string): {
        url: string;
        designApiUrl?: string;
    } {
        const designEnabled = this.moduleAccess.hasDesign2;
        if (designEnabled) {
            return {
                url: url.replace('/api/v4/', '/api/v5/'),
                designApiUrl: PI.appSettings.designApiUrl
            };
        }
        return {
            url,
            designApiUrl: null
        };
    }
}
